import { useState } from "react";
import { useSnapshot } from "valtio";
import { Spinner, Alert, Button, Form, InputGroup, ButtonGroup } from "react-bootstrap";
import { launchStore, launchActions } from "src/modules/engagement/launchStore";
import { usePopulation } from "./usePopulation";
import { PopulationStats } from "./PopulationStats";
import { PopulationControl } from "./PopulationControl";
import { PopulationList } from "./PopulationList";
import { ContractListSizePanel } from "./ContractListSizePanel";
import "./contractListSize.css";

const DEFAULT_PAGE_SIZE = 25;

interface PopulationSectionProps {
  /* The campaign whose population this is. Passed in rather than read from
     launchStore.currentLaunchId: that is ambient state set by a useEffect in
     CampaignStep, so it is null on the first render after a direct URL load and
     the whole panel silently returned null. The launch is already in hand here —
     PublishersCard beside us takes it the same way. */
  launchId: number;
}

export default function PopulationSection({ launchId }: PopulationSectionProps) {
  const { audienceRefreshTrigger } = useSnapshot(launchStore);

  const [expanded, setExpanded] = useState(false);
  const [activeAudienceId, setActiveAudienceId] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [consumerIdInput, setConsumerIdInput] = useState("");
  const [consumerId, setConsumerId] = useState<number | null>(null);
  const [controlFilter, setControlFilter] = useState<"all" | "treatment" | "control">("all");

  const { data, loading, error } = usePopulation({
    launchId,
    limit: pageSize,
    offset: page * pageSize,
    audienceId: activeAudienceId,
    consumerId,
    // "all" | "treatment" | "control" — the backend scopes the whole panel by it.
    controlMode: controlFilter,
    refreshKey: audienceRefreshTrigger,
  });

  const toggleAudience = (audienceId: number) => {
    setActiveAudienceId((prev) => (prev === audienceId ? null : audienceId));
    setPage(0);
  };

  const changePageSize = (size: number) => {
    setPageSize(size);
    setPage(0);
  };

  const submitConsumerSearch = () => {
    const trimmed = consumerIdInput.trim();
    const parsed = trimmed === "" ? null : Number(trimmed);
    setConsumerId(parsed !== null && Number.isFinite(parsed) ? parsed : null);
    setPage(0);
  };

  const clearConsumerSearch = () => {
    setConsumerIdInput("");
    setConsumerId(null);
    setPage(0);
  };

  const changeControlFilter = (value: "all" | "treatment" | "control") => {
    setControlFilter(value);
    setPage(0);
  };

  const count =
    data?.summary.total_population ??
    data?.summary.total_members ??
    data?.total ??
    0;

  const hasSegment = !!data && data.population_segment_id !== null;
  // A holdout can exist without the control policy being enabled — old-platform
  // members can be loaded is_control=true directly. So gate the Control filter on
  // the actual count, never on control_group_enabled.
  const hasHoldout = !!data && data.summary.control_group > 0;

  return (
    <>
      <div
        className="bg-light py-3 px-3 rounded mb-3 d-flex align-items-center justify-content-between"
        style={{ minHeight: "50px" }}
      >
        <h5 className="my-0 fw-bold fs-18">
          Population
          {data && (
            <span className="text-muted fw-normal ms-2" style={{ fontSize: "0.9rem" }}>
              — {count.toLocaleString()} {count === 1 ? "person" : "people"}
            </span>
          )}
        </h5>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => setExpanded((e) => !e)}
          className="d-flex align-items-center gap-1"
        >
          <i className={`bi ${expanded ? "bi-chevron-up" : "bi-chevron-down"}`} />
          {expanded ? "Hide details" : "Show details"}
        </Button>
      </div>

      {expanded && (
        <>
          {loading && (
            <div className="d-flex justify-content-center p-4">
              <Spinner animation="border" role="status" />
            </div>
          )}

          {error && !loading && <Alert variant="danger" className="mb-3">{error}</Alert>}

          {data && !error && (
            <>
              {/* The contracted ceiling, for reference beside the real number. */}
              <ContractListSizePanel
                contractListSize={data.summary.contract_list_size}
                sizedBy={data.summary.contract_sized_by}
                actualSize={count}
              />

              {/* Control group is a fixed property of the experiment — kept ABOVE the
                  filters so it reads as context that the toggle doesn't change. */}
              <PopulationControl
                key={`${data.control_group_enabled}|${data.control_group_pct}`}
                launchId={launchId}
                hasSegment={data.population_segment_id !== null}
                enabled={data.control_group_enabled}
                pct={data.control_group_pct}
                controlCount={data.summary.control_group}
                onApplied={() => launchActions.triggerAudienceRefresh()}
              />

              {/* Population-wide view selector — scopes the stats + list below. */}
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <span className="text-uppercase text-muted fw-semibold" style={{ fontSize: "0.72rem", letterSpacing: "0.04em" }}>
                  View
                </span>
                <ButtonGroup size="sm">
                  {(["all", "treatment", "control"] as const).map((v) => {
                    // "control"/"treatment" need a segment; "control" additionally
                    // needs an actual holdout (0 => the filter can only be empty).
                    const disabled =
                      v === "all" ? false : !hasSegment || (v === "control" && !hasHoldout);
                    return (
                      <Button
                        key={v}
                        variant={controlFilter === v ? "primary" : "outline-primary"}
                        disabled={disabled}
                        title={v === "control" && hasSegment && !hasHoldout
                          ? "No control group — enable the holdout above or load control members"
                          : undefined}
                        onClick={() => changeControlFilter(v)}
                      >
                        {v === "all" ? "All" : v === "treatment" ? "Treatment" : "Control"}
                      </Button>
                    );
                  })}
                </ButtonGroup>
              </div>

              {activeAudienceId !== null && (
                <div className="mb-2">
                  <Button variant="outline-secondary" size="sm" onClick={() => { setActiveAudienceId(null); setPage(0); }}>
                    Clear audience filter
                  </Button>
                </div>
              )}
              <PopulationStats
                summary={data.summary}
                activeAudienceId={activeAudienceId}
                onToggleAudience={toggleAudience}
              />
              {/* Consumer-ID lookup lives right above the list it filters. */}
              <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                <InputGroup size="sm" style={{ maxWidth: 260 }}>
                  <Form.Control
                    placeholder="Search by consumer ID"
                    value={consumerIdInput}
                    inputMode="numeric"
                    onChange={(e) => setConsumerIdInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") submitConsumerSearch(); }}
                  />
                  <Button variant="outline-primary" onClick={submitConsumerSearch}>
                    <i className="bi bi-search" />
                  </Button>
                  {consumerId !== null && (
                    <Button variant="outline-secondary" onClick={clearConsumerSearch}>
                      Clear
                    </Button>
                  )}
                </InputGroup>
                {consumerId !== null && (
                  <span className="text-muted small">
                    {data.total > 0
                      ? `Showing consumer #${consumerId}`
                      : `No consumer #${consumerId} in this population`}
                  </span>
                )}
              </div>
              <PopulationList
                launchId={launchId}
                consumers={data.items}
                total={data.total}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={changePageSize}
              />
            </>
          )}
        </>
      )}
    </>
  );
}
